import { useState, type ReactNode } from "react"
import { Sidebar } from "./sidebar"
import { MobileTopBar } from "./mobile-top-bar"
import { NavDrawer } from "./nav-drawer"
import { FlashBanner } from "./flash-banner"
import { cn } from "@/lib/cn"

type Props = {
  children: ReactNode
  fullBleed?: boolean
}

// Top-level shell — responsive, mobile-first.
//
// Layout:
//   - Below `lg:` (1024 px): sticky <MobileTopBar> with a hamburger that
//     opens <NavDrawer>; main content is full-width.
//   - At `lg:` and up: persistent <Sidebar> on the left (always visible,
//     w-72), main content fills the rest. The sidebar self-hides below
//     lg via its own `hidden lg:flex` classes.
//   - Inner main container caps at max-w-7xl on desktop, grows to
//     max-w-[1680px] at 3xl (1800 px) and max-w-[2040px] at 4xl
//     (2200 px). Beyond that, margin grows on the sides — line-lengths
//     stay readable on 49″ ultrawide displays.
//
// FlashBanner is mounted globally so `flash.notice` / `flash.alert` from
// Portal::ApplicationController#inertia_share surface on EVERY page.
export function AppShell({ children, fullBleed = false }: Props) {
  const [navOpen, setNavOpen] = useState(false)

  return (
    <div className="min-h-screen text-app-text lg:flex">
      <MobileTopBar onOpenNav={() => setNavOpen(true)} />
      <Sidebar />
      <NavDrawer open={navOpen} onClose={() => setNavOpen(false)} />

      <main
        className={cn(
          "min-w-0 flex-1",
          fullBleed ? "px-0 py-0" : "px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10 3xl:px-12 4xl:px-16",
        )}
      >
        <div
          className={cn(
            "w-full",
            fullBleed ? "min-h-screen" : "mx-auto max-w-7xl space-y-6 3xl:max-w-[1680px] 4xl:max-w-[2040px]",
          )}
        >
          <FlashBanner />
          {children}
        </div>
      </main>
    </div>
  )
}
